import { Unsubscribe, doc, getDoc, setDoc, onSnapshot, collection, getDocs, QuerySnapshot, DocumentSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

import { Conference } from '../types';

export async function getConferenceCodes(): Promise<string[]> {
   try {
      const confRef = collection(db, 'conferences');
      const confDocs = await getDocs(confRef);
      return confDocs.docs.map(doc => doc.id);
   } catch (error) {
      console.error(error);
      return [];
   }
}

// mutation to get conference data
export async function getConferenceData(conferenceCode: string): Promise<Conference | null> {
    try {
      if (!conferenceCode) {
         throw new Error("Invalid conference code");
      }
      const confRef = doc(db, 'conferences', conferenceCode);
      const confDoc = await getDoc(confRef);
      
      if (confDoc.exists()) {
         return confDoc.data() as Conference;
      } else {
         return null;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
 }
 

// Function to create an onSnapshot listener for all conferences
export function subscribeToConferences(callback: (data: Conference[]) => void): Unsubscribe {
    const conferencesRef = db.collection('conferences');
  
    // Set up onSnapshot listener and provide the callback function
    const unsubscribe = onSnapshot(conferencesRef, (snapshot: QuerySnapshot) => {
      const conferences: Conference[] = snapshot.docs.map(doc => ({
          conferenceCode: doc.id,
          conferenceName: doc.data().conferenceName,
          prices: doc.data().prices,
          questions: doc.data().questions || [],
      }));
      // Pass the document data to the callback
      callback(conferences);
    });
  
    // Return the unsubscribe function to allow unsubscribing later
    return unsubscribe;
}
 
// Function to create an onSnapshot listener for a specific conference
export function subscribeToConference(confId: string, callback: (data: Conference | null) => void): Unsubscribe {
    const conferenceRef = doc(db, 'conferences', confId);
  
    // Set up onSnapshot listener and provide the callback function
    const unsubscribe = onSnapshot(conferenceRef, (docSnapshot: DocumentSnapshot) => {
      if (docSnapshot.exists()) {
          // Pass the document data to the callback
          const conferenceData: Conference = {
             conferenceCode: docSnapshot.id,
             conferenceName: docSnapshot.data().conferenceName,
             prices: docSnapshot.data().prices,
          };
          callback(conferenceData);
       } else {
          // Handle document not found
          console.error(`Conference with ID ${confId} not found`);
       }
    });
  
    // Return the unsubscribe function to allow unsubscribing later
    return unsubscribe;
}

// Function to add a new conference
export async function updateDetails(conferenceCode: string, conferenceName: string, prices: Record<string, number>): Promise<void> {
   try {
      if(!conferenceCode || !conferenceName || typeof prices !== 'object') {
         throw new Error("Invalid input: conferenceCode, conferenceName, and prices are required.");
      }
      // Get a reference to the conference document
      const confRef = doc(db, 'conferences', conferenceCode);
      // Check if the conference exists
      const confDoc = await getDoc(confRef);
      if(!confDoc.exists()) {
         throw new Error(`Conference with code ${conferenceCode} not found`);
      }
      // update the prices
      await setDoc(confRef, {
         conferenceName: conferenceName,
         prices: prices,
      });

    } catch (error) {
      console.error(error);
      throw error; // Rethrow the error to indicate that the synchronization failed
    }
}